import React from 'react';
import { Link } from 'react-router-dom';
import { isetFormConfig } from '../isetFormConfig';
import { getSectionCompleteness } from '../utils/sectionCompleteness';
import { normalizeLegacyFields } from '../utils/normalizeLegacyFields';
import {
  getParticipantStatusBannerClass,
  getParticipantStatusPresentation,
} from '../utils/participantStatus';

const normalizeExternalStatus = (status) => {
  if (!status) return null;
  const token = String(status).trim().toLowerCase();
  if (!token) return null;
  return token.replace(/\s+/g, '_');
};

function ApplicationCard({
  application,
  language,
  onResumeDraft,
  onDeleteDraft,
  deleteDisabled = false,
  statusPresentation,
  statusMessage,
  statusNextSteps,
  statusActionHref,
  statusActionLabel
}) {
  const {
    tracking_id,
    created_at,
    status: statusProp,
    external_status: externalStatusProp
  } = application;
  const detailAvailable = statusProp !== 'draft'
    && application.detail_available !== false
    && Boolean(tracking_id);

  const normalizedExternal = normalizeExternalStatus(externalStatusProp || statusProp);
  const status = statusProp === 'draft'
    ? 'draft'
    : (normalizedExternal || 'review_in_progress');

  const progress = application.progress;
  const hasDraftProgress = progress && Number.isInteger(progress.requiredTotal)
    && Number.isInteger(progress.requiredRemaining) && progress.requiredTotal >= 0
    && progress.requiredRemaining >= 0 && progress.requiredRemaining <= progress.requiredTotal
    && typeof progress.readyToSubmit === 'boolean';

  // Dynamic drafts expose server-computed progress, never an answer payload.
  let sectionStatusCache = [];
  let allSectionsComplete = false;
  let docsUploaded = false;
  let draftComplete = false;
  if (status === 'draft') {
    if (application.dynamic) {
      draftComplete = Boolean(hasDraftProgress && progress.readyToSubmit);
    } else {
      const flatData = { ...(application.payload || application) };
      const normalized = normalizeLegacyFields(flatData);
      sectionStatusCache = getSectionCompleteness(isetFormConfig, normalized);
      allSectionsComplete = sectionStatusCache.length > 0 && sectionStatusCache.every(sec => sec.complete);
      docsUploaded = (application.document_refs && Object.keys(application.document_refs).length > 0) || (application.docRefs && Object.keys(application.docRefs).length > 0);
      draftComplete = allSectionsComplete && docsUploaded;
    }
  }

  const defaultTitle = language === 'fr'
    ? 'Demande PATH'
    : 'PATH application';
  const resolvedStatusPresentation = status === 'draft'
    ? null
    : (statusPresentation || getParticipantStatusPresentation(status, language));
  const statusSummary = resolvedStatusPresentation?.summary || statusMessage || '';
  const nextSteps = resolvedStatusPresentation?.nextSteps || statusNextSteps || '';
  const actionHref = resolvedStatusPresentation?.actionHref || statusActionHref || null;
  const actionLabel = resolvedStatusPresentation?.actionLabel || statusActionLabel || null;

  const statusTitle = status === 'draft'
    ? (language === 'fr' ? 'Demande ISET sauvegardée' : 'Saved ISET application')
    : (resolvedStatusPresentation?.cardHeading || defaultTitle);

  const readableStatus = status === 'draft'
    ? (draftComplete
      ? (language === 'fr' ? 'Prête à soumettre' : 'Ready to submit')
      : (allSectionsComplete && !docsUploaded
        ? (language === 'fr' ? 'Documents encore requis' : 'Documents still needed')
        : (language === 'fr' ? 'En cours' : 'In progress')))
    : (resolvedStatusPresentation?.label || (status ? status.replace(/_/g, ' ') : ''));

  // Section completeness for draft applications
  let sectionChecklist = null;
  if (status === 'draft') {
    const sectionStatus = Array.isArray(sectionStatusCache) ? sectionStatusCache : [];
    const mandatorySections = sectionStatus.filter(sec => {
      if (!sec || typeof sec !== 'object') return false;
      if (typeof sec.isMandatory === 'boolean') return sec.isMandatory;
      if (typeof sec.requiredCount === 'number') return sec.requiredCount > 0;
      return Array.isArray(sec.missingFields) && sec.missingFields.length > 0;
    });
    const mandatoryTotal = application.dynamic ? progress?.requiredTotal : mandatorySections.length;
    const mandatoryRemaining = application.dynamic ? progress?.requiredRemaining : mandatorySections.filter(sec => !sec.complete).length;
    const fallbackCompleted = sectionStatus.filter(s => s.complete).length;
    const fallbackTotal = sectionStatus.length;
    const progressLabel = application.dynamic || mandatoryTotal > 0
      ? (language === 'fr'
        ? `${mandatoryRemaining} étape${mandatoryRemaining > 1 ? 's' : ''} obligatoire${mandatoryRemaining > 1 ? 's' : ''} restante${mandatoryRemaining > 1 ? 's' : ''} sur ${mandatoryTotal}`
        : `${mandatoryRemaining} of ${mandatoryTotal} required steps remaining`)
      : (language === 'fr'
        ? `${fallbackCompleted} section${fallbackCompleted > 1 ? 's' : ''} complétée${fallbackCompleted > 1 ? 's' : ''} sur ${fallbackTotal}`
        : `${fallbackCompleted} of ${fallbackTotal} sections complete`);
    if (application.dynamic && !hasDraftProgress) {
      sectionChecklist = (
        <div className="govuk-warning-text govuk-!-margin-top-2">
          <span className="govuk-warning-text__icon" aria-hidden="true">!</span>
          <strong className="govuk-warning-text__text">
            {language === 'fr'
              ? 'La progression de la demande est temporairement indisponible. Vous pouvez reprendre votre demande sauvegardée.'
              : 'Application progress is temporarily unavailable. You can continue your saved application.'}
          </strong>
        </div>
      );
    } else {
      sectionChecklist = (
        <div>
          <p className="govuk-body" style={{ marginBottom: '0.5em' }}>
            {progressLabel}
          </p>
        </div>
      );
    }
  }

  const bannerClassName = status === 'draft'
    ? 'govuk-notification-banner app-dashboard-card'
    : getParticipantStatusBannerClass(status);
  const bannerId = tracking_id || application.application_id || 'draft';

  return (
    <div
      className={bannerClassName}
      role="region"
      aria-labelledby={`banner-title-${bannerId}`}
      data-module="govuk-notification-banner"
    >
      <div className="govuk-notification-banner__header">
        <h2 className="govuk-notification-banner__title" id={`banner-title-${bannerId}`}>
          {statusTitle}
        </h2>
      </div>
      <div className="govuk-notification-banner__content">
        {statusSummary && status !== 'draft' && (
          <p className="govuk-body govuk-!-font-weight-bold govuk-!-margin-bottom-3">
            {statusSummary}
            {actionHref && actionLabel && (
              <>
                {' '}
                <Link to={actionHref} className="govuk-link">
                  {actionLabel}
                </Link>
              </>
            )}
          </p>
        )}
        {nextSteps && status !== 'draft' && (
          <p className="govuk-body govuk-!-margin-bottom-3">{nextSteps}</p>
        )}
        <dl className="govuk-summary-list" style={{ marginBottom: '1em' }}>
          <div className="govuk-summary-list__row">
            <dt className="govuk-summary-list__key" style={{ minWidth: 120 }}>
              {language === 'fr' ? 'ID de suivi' : 'Tracking ID'}
            </dt>
            <dd className="govuk-summary-list__value" style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.1em' }}>
              <span style={{ userSelect: 'all' }}>{tracking_id || '—'}</span>
              {tracking_id && (
                <button
                  type="button"
                  className="govuk-link govuk-!-margin-left-2"
                  style={{ fontSize: '0.9em', padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                  onClick={() => navigator.clipboard && tracking_id && navigator.clipboard.writeText(tracking_id)}
                  aria-label={language === 'fr' ? 'Copier l’ID de suivi' : 'Copy Tracking ID'}
                >
                  {language === 'fr' ? 'Copier' : 'Copy'}
                </button>
              )}
            </dd>
          </div>
          <div className="govuk-summary-list__row">
            <dt className="govuk-summary-list__key">{language === 'fr' ? 'Mis à jour le' : 'Last updated'}</dt>
            <dd className="govuk-summary-list__value">{application.last_updated ? new Date(application.last_updated).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA') : '—'}</dd>
          </div>
          {status !== 'draft' && created_at && (
            <div className="govuk-summary-list__row">
              <dt className="govuk-summary-list__key">{language === 'fr' ? 'Soumis le' : 'Submitted on'}</dt>
              <dd className="govuk-summary-list__value">{new Date(created_at).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')}</dd>
            </div>
          )}
          <div className="govuk-summary-list__row">
            <dt className="govuk-summary-list__key">{language === 'fr' ? 'Statut' : 'Status'}</dt>
            <dd className="govuk-summary-list__value">{readableStatus}</dd>
          </div>
        </dl>
        {status === 'draft' && sectionChecklist}

        <div className="govuk-button-group">
          {status === 'draft' && (
            <Link
              to="#"
              className="govuk-button"
              onClick={onResumeDraft ? (e) => { e.preventDefault(); onResumeDraft(); } : undefined}
            >
              {language === 'fr' ? 'Reprendre la demande' : 'Continue application'}
            </Link>
          )}
          {status !== 'draft' && detailAvailable && (
            <Link
              to={`/submission/${tracking_id}`}
              className="govuk-button govuk-button--secondary"
            >
              {language === 'fr' ? 'Voir les détails' : 'View Details'}
            </Link>
          )}
          {status !== 'draft' && (
            <Link
              to="/messages/new"
              className="govuk-button govuk-button--secondary"
            >
              {language === 'fr' ? 'Envoyer un message' : 'Send a message'}
            </Link>
          )}
          {status === 'draft' && (
            <button
              className="govuk-button govuk-button--secondary"
              disabled={deleteDisabled}
              onClick={(e) => {
                e.preventDefault();
                if (onDeleteDraft) onDeleteDraft();
              }}
            >
              {language === 'fr' ? 'Supprimer la demande sauvegardée' : 'Delete saved application'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ApplicationCard;
